import type { HelpTopicDetail } from './help-topic-detail.types';
import {
  API_DOC_PATHS,
  API_V1_ROUTE_MODULES,
  API_V2_ROUTE_MODULES,
  MCP_HTTP_ENDPOINTS,
} from '@/lib/api-reference';
function d(
  summary: string,
  o: {
    h?: [string, string][];
    st?: [string, string][];
    se?: { hd: string; p: string[]; b?: string[] }[];
    re?: { label: string; type: 'image' | 'video' | 'page'; pathOrUrl: string; note?: string }[];
    q?: { question: string; options: string[]; correctOptionIndex: number; explanation: string }[];
    t?: string[];
    r?: string[];
  } = {},
): HelpTopicDetail {
  return {
    summary,
    highlights: (o.h ?? []).map(([label, value]) => ({ label, value })),
    steps: (o.st ?? []).map(([title, description]) => ({ title, description })),
    sections: (o.se ?? []).map((x) => ({
      heading: x.hd,
      paragraphs: x.p,
      bullets: x.b,
    })),
    resources: o.re ?? [],
    quiz: o.q ?? [],
    tips: o.t ?? [],
    relatedTopicIds: o.r ?? [],
  };
}

/** Fiches détaillées — contenu rédactionnel aligné sur les fonctionnalités Lyrra. */
export const HELP_TOPIC_DETAILS: Record<string, HelpTopicDetail> = {
  /* demarrage-tableau-bord : player interactif (HelpPage). */
  /* demarrage-navigation : player interactif (HelpPage). */

  'demarrage-profil': d(
    'Votre profil regroupe identité, préférences d’affichage (langue) et paramètres de compte selon ce que votre administrateur a activé.',
    {
      h: [
        ['Données', 'Nom, e-mail, photo éventuelle'],
        ['Sécurité', 'Mot de passe / SSO selon config'],
        ['Langue', 'Interface i18n Lyrra'],
      ],
      st: [
        [
          'Ouvrir les paramètres',
          'Utilisez l’entrée « Paramètres » ou votre avatar dans l’en-tête, selon la version du shell applicatif.',
        ],
        [
          'Mettre à jour les informations',
          'Certains champs sont verrouillés par l’administrateur (ex. e-mail professionnel) : en cas de besoin, contactez le support interne.',
        ],
        [
          'Choisir la langue',
          'Le changement de langue recharge les libellés de l’interface ; le contenu pédagogique des parcours reste celui saisi par les auteurs.',
        ],
      ],
      se: [
        {
          hd: 'Notifications',
          p: [
            'Les notifications in-app et e-mail dépendent des modules activés (publication, invitations, rapports).',
          ],
        },
      ],
      t: [
        'Utilisez un e-mail professionnel stable : les apprenants et les liens magiques peuvent s’y rattacher.',
      ],
      r: ['donnees-mots-de-passe', 'demarrage-roles'],
    },
  ),

  'demarrage-roles': d(
    'Lyrra distingue plusieurs rôles (admin plateforme, admin établissement, formateur, RH, apprenant invité via lien, etc.). Chaque rôle voit un sous-ensemble de menus et d’actions.',
    {
      h: [
        ['Formateur', 'Création EduFlow, médias, analytics'],
        ['Institution', 'Utilisateurs, marque, corbeille'],
        ['Apprenant', 'Player, progression, certificats'],
      ],
      st: [
        [
          'Identifier votre rôle',
          'Si une fonction manque (ex. « Établissement »), votre compte n’a pas le rôle requis — demandez à l’administrateur Lyrra de votre structure.',
        ],
        [
          'Collaborateur sur un parcours',
          'Un utilisateur ajouté comme collaborateur EduFlow peut éditer le parcours sans être propriétaire : les droits exacts dépendent du niveau de collaboration.',
        ],
        [
          'Apprenant via participant',
          'Les apprenants accèdent souvent par lien avec jeton ; leur expérience est centrée sur le player, pas sur le designer.',
        ],
      ],
      se: [
        {
          hd: 'Bonnes pratiques organisationnelles',
          p: [
            'Limitez le nombre de comptes administrateur ; créez des comptes formateur pour la production quotidienne.',
            'Documentez en interne qui valide la publication des parcours sensibles (conformité, image de marque).',
          ],
        },
      ],
      t: [
        'Ne partagez jamais votre session : utilisez des comptes nominatifs pour la traçabilité Qualiopi / audit.',
      ],
      r: ['inst-collaborateurs', 'qualite-tracabilite'],
    },
  ),

  'eduflow-concept': d(
    'Le concepteur EduFlow affiche un canevas infini : blocs reliés par des arêtes, zoom, grille optionnelle, minimap et panneau de propriétés à droite.',
    {
      h: [
        ['Canvas', 'Glisser-déposer, zoom molette'],
        ['Blocs', 'Palette latérale ou ajout'],
        ['États', 'Sélection, multi-sélection partielle'],
      ],
      st: [
        [
          'Ajouter un bloc',
          'Choisissez un type dans la palette (Départ, Texte, Quiz, Fin, etc.) et placez-le sur le canevas ; reliez-le aux autres via les poignées de connexion.',
        ],
        [
          'Ajuster la vue',
          'Utilisez la molette pour zoomer, l’espace ou l’outil main pour déplacer le canevas ; la minimap aide sur les grands parcours.',
        ],
        [
          'Éditer le contenu',
          'Cliquez sur un bloc : le panneau de droite affiche titre, contenu et réglages spécifiques au type de bloc.',
        ],
      ],
      se: [
        {
          hd: 'Performances',
          p: [
            'Les très gros parcours (dizaines de blocs) restent utilisables mais privilégiez la clarté pédagogique et des sous-parcours si votre modèle le permet.',
          ],
          b: [
            'Grille et repères : activables depuis les contrôles du bas du canevas (affichage type « designer »).',
            'Sauvegarde : enregistrez régulièrement ; la restauration de version est disponible via le panneau Versions lorsqu’il est activé.',
          ],
        },
      ],
      t: [
        'Nommez chaque bloc tôt : le panneau Validation exige des titres non vides sur tous les blocs.',
      ],
      r: ['eduflow-blocs', 'eduflow-validation'],
    },
  ),

  'eduflow-blocs': d(
    'Chaque type de bloc porte des champs différents : texte riche, média intégré, quiz à choix multiples, évaluation notée, minuterie, e-mail, sous-parcours, etc.',
    {
      h: [
        ['Contenu', 'Texte, PDF, audio, vidéo'],
        ['Interaction', 'Quiz, sondage, évaluation'],
        ['Flux', 'Départ, Fin, boucle, branche'],
      ],
      st: [
        [
          'Choisir le bon bloc',
          'Commencez toujours par un bloc Départ et terminez par au moins un bloc Fin ; les blocs de contenu portent le savoir, les blocs d’évaluation vérifient les acquis.',
        ],
        [
          'Renseigner le titre',
          'Le titre est obligatoire pour la validation et pour l’accessibilité (apprenants lecteurs d’écran).',
        ],
        [
          'Configurer les médias',
          'Importez depuis la bibliothèque média ou collez une URL selon le type ; vérifiez les formats supportés par le navigateur (MP4, MP3, etc.).',
        ],
      ],
      se: [
        {
          hd: 'Blocs d’évaluation',
          p: [
            'Les quiz et évaluations peuvent avoir une notation et des seuils ; renseignez la partie « scoring » ou équivalent pour éviter les avertissements de validation.',
          ],
        },
      ],
      t: [
        'Réutilisez la bibliothèque pour ne pas dupliquer les mêmes fichiers lourds dans chaque bloc.',
      ],
      r: ['eval-quiz', 'medias-bibliotheque'],
    },
  ),

  'eduflow-branches': d(
    'Dès qu’un bloc a plusieurs sorties (chemins distincts), Lyrra impose que toutes les branches puissent rejoindre un point de convergence commun avant la fin — pour éviter les parcours orphelins.',
    {
      h: [
        ['Limite', 'Nombre max de sorties par bloc'],
        ['Convergence', 'Bloc commun atteignable'],
        ['Validation', 'Erreurs bloquantes'],
      ],
      st: [
        [
          'Créer plusieurs sorties',
          'Reliez chaque branche à la cible suivante depuis les poignées du bloc source ; donnez des libellés clairs aux choix si le player les affiche.',
        ],
        [
          'Assurer la convergence',
          'Ajoutez un bloc de type adapté (ex. fusion, ou nœud commun) tel que toutes les branches y accèdent avant d’atteindre la Fin.',
        ],
        [
          'Vérifier avec Validation',
          'Ouvrez le panneau Validation : les erreurs « branche sans fin » ou « sorties multiples sans convergence » indiquent précisément le bloc concerné.',
        ],
      ],
      se: [
        {
          hd: 'Pourquoi cette règle',
          p: [
            'Elle garantit qu’aucun apprenant ne reste coincé sur une branche sans issue ni retour vers le fil principal.',
          ],
        },
      ],
      t: [
        'Pour un simple oui/non, deux sorties vers le même bloc suivant peuvent suffire si votre scénario pédagogique le permet — l’important est la cohérence du graphe.',
      ],
      r: ['eduflow-validation', 'eduflow-concept'],
    },
  ),

  'eduflow-validation': d(
    'Le panneau Validation analyse le graphe (Départ unique, Fin présente, titres, connexions sortantes, limites de sorties, convergence, cycles signalés en avertissement).',
    {
      h: [
        ['Erreurs', 'Publication bloquée'],
        ['Avertissements', 'Risques pédagogiques'],
        ['Clic', 'Focus sur le bloc'],
      ],
      st: [
        [
          'Ouvrir le panneau',
          'Utilisez le bouton Validation dans la barre d’outils du designer ; le panneau couvre toute la largeur du volet droit.',
        ],
        [
          'Corriger les erreurs',
          'Cliquez une ligne d’erreur : le bloc concerné est sélectionné et le canevas peut être recentré automatiquement.',
        ],
        [
          'Traiter les avertissements',
          'Les avertissements (ex. cycle, texte vide) ne bloquent pas toujours la publication mais méritent une relecture.',
        ],
      ],
      se: [
        {
          hd: 'Prévisualisation',
          p: [
            'Même avec zéro erreur, testez le player en conditions réelles (compte apprenant ou lien de prévisualisation) avant diffusion large.',
          ],
        },
      ],
      t: [
        'Gardez le panneau Validation ouvert pendant les grosses restructurations de parcours.',
      ],
      r: ['pub-statuts', 'eduflow-branches'],
    },
  ),

  'eduflow-objectifs': d(
    'Les objectifs pédagogiques permettent de structurer le référentiel (savoir, savoir-faire, savoir-être, niveaux Bloom) et de lier des blocs à ces objectifs pour la traçabilité.',
    {
      h: [
        ['Référentiel', 'Objectifs par formation'],
        ['Liaisons', 'Blocs ↔ objectifs'],
        ['Qualité', 'Preuves pour audit'],
      ],
      st: [
        [
          'Ouvrir le panneau Objectifs',
          'Depuis le designer, activez le panneau dédié aux objectifs ; créez ou importez la liste pour le parcours courant.',
        ],
        [
          'Rédiger des objectifs mesurables',
          'Formulez des verbes d’action observables (« identifier », « réaliser », « argumenter ») plutôt que des intentions vagues.',
        ],
        [
          'Lier aux blocs',
          'Associez chaque objectif aux blocs qui le couvrent (contenu + évaluation) pour faciliter le dossier qualité.',
        ],
      ],
      se: [
        {
          hd: 'Qualiopi',
          p: [
            'Les modules Qualiopi de Lyrra s’appuient sur ces liens pour pré-remplir ou guider les indicateurs — adaptez le texte aux exigences de votre certificateur.',
          ],
        },
      ],
      t: [
        'Mettez à jour les objectifs quand le parcours change substantiellement (nouveau module, nouvelle certification).',
      ],
      r: ['qualite-tracabilite', 'qualite-qualiopi'],
    },
  ),

  'eduflow-ia': d(
    'L’assistant IA du designer propose des modifications ou ajouts de blocs à partir de consignes en langage naturel ; les opérations sont présentées pour application explicite (boutons valider / ignorer).',
    {
      h: [
        ['Contrôle', 'Validation humaine'],
        ['Contexte', 'Blocs & objectifs envoyés au modèle'],
        ['Traçabilité', 'Historique de conversation locale'],
      ],
      st: [
        [
          'Formuler une consigne',
          'Soyez précis : « ajouter un quiz de 3 QCM après le bloc X sur la sécurité » donne de meilleurs résultats qu’« améliore le cours ».',
        ],
        [
          'Relire les opérations',
          'Avant d’appliquer, vérifiez chaque opération proposée : l’IA peut halluciner des titres ou des connexions incorrectes.',
        ],
        [
          'Valider la conformité',
          'Pour les sujets réglementés (santé, finance), un expert métier doit valider le fond ; l’IA accélère la mise en forme, pas la responsabilité éditoriale.',
        ],
      ],
      se: [
        {
          hd: 'Données envoyées',
          p: [
            'Le chat peut inclure la structure du parcours et les objectifs pédagogiques pour contextualiser les réponses — consultez la politique de confidentialité de votre instance.',
          ],
        },
      ],
      t: [
        'Enregistrez le parcours avant des opérations IA massives pour pouvoir revenir en arrière via les versions.',
      ],
      r: ['eduflow-concept', 'eduflow-validation'],
    },
  ),

  'pub-statuts': d(
    'Un parcours EduFlow traverse des statuts (brouillon, publié selon votre workflow). Publier rend le contenu disponible pour les apprenants autorisés ; les modifications ultérieures peuvent nécessiter une republication ou une mise à jour versionnée.',
    {
      h: [
        ['Brouillon', 'Édition sans impact apprenant'],
        ['Publié', 'Player à jour pour nouveaux accès'],
        ['Versions', 'Historique si activé'],
      ],
      st: [
        ['Tester avant diffusion', 'Utilisez la prévisualisation ou un compte test pour valider médias et navigation.'],
        ['Enregistrer', 'Sauvegardez avant de changer de statut ; les gros parcours peuvent prendre quelques secondes.'],
        ['Communiquer', 'Prévenez les formateurs ou RH lors d’une republication majeure (contenu certifiant).'],
      ],
      se: [
        {
          hd: 'Impact sur les apprenants en cours',
          p: [
            'Selon la configuration, les apprenants déjà engagés peuvent conserver une version ou basculer sur la nouvelle — clarifiez la règle en interne.',
          ],
          b: ['Documentez la date de version dans votre LMS ou intranet si vous dupliquez l’information.'],
        },
      ],
      t: ['Évitez de publier un vendredi soir sans garde : en cas de régression, personne ne pourra corriger.'],
      r: ['pub-liens', 'eduflow-validation'],
    },
  ),

  'pub-liens': d(
    'Lyrra génère des liens d’accès pour apprenants (souvent avec jeton ou code), parfois des URL présentateur ou de prévisualisation pour les concepteurs.',
    {
      h: [
        ['Apprenant', 'Lien personnel ou générique'],
        ['Sécurité', 'Ne pas exposer sur le web public'],
        ['Expiration', 'Selon politique institution'],
      ],
      st: [
        ['Générer le lien', 'Depuis la fiche formation ou la section partage, créez le lien ; copiez-le dans votre LMS, e-mail ou QR code.'],
        ['Restreindre l’accès', 'Associez le parcours à un groupe ou une campagne si votre organisation impose un périmètre.'],
        ['Révoquer', 'En cas de fuite, régénérez les jetons ou désactivez l’accès côté administration.'],
      ],
      se: [{ hd: 'Conformité', p: ['Les liens font partie des données d’accès : traçabilité et durée de vie doivent être documentées pour le RGPD.'] }],
      t: ['Ne postez jamais un lien apprenant sur un réseau social ouvert sans anonymisation prévue.'],
      r: ['app-inscriptions', 'donnees-rgpd'],
    },
  ),

  'pub-catalogue': d(
    'Le catalogue institutionnel regroupe les offres visibles selon les règles de votre établissement (formation interne, vente, catalogue public).',
    {
      h: [
        ['Visibilité', 'Par rôle / groupe'],
        ['Métadonnées', 'Titre, durée, public'],
        ['Accès', 'Inscription ou lien selon config'],
      ],
      st: [
        ['Publier dans le catalogue', 'Renseignez les champs obligatoires (titre, description courte, vignette) pour un affichage cohérent.'],
        ['Aligner avec la communication', 'Les textes catalogue sont souvent la première impression — harmonisez avec votre site vitrine.'],
        ['Mesurer', 'Les analytiques ou inscriptions permettent de suivre l’intérêt pour chaque offre.'],
      ],
      se: [{ hd: 'Multi-offres', p: ['Si plusieurs parcours se chevauchent, clarifiez le positionnement (niveau, prérequis) pour éviter les mauvaises inscriptions.'] }],
      t: ['Mettez à jour le catalogue quand un parcours est retiré ou remplacé pour ne pas inscrire sur une ancienne version.'],
      r: ['marque-landing', 'pub-liens'],
    },
  ),

  'app-inscriptions': d(
    'La liste des apprenants (ou participants) sur un EduFlow regroupe e-mails, noms, progression, dernière activité et parfois les sessions de blocs détaillées.',
    {
      h: [
        ['Vue liste', 'Recherche & filtres'],
        ['Données', 'Progression JSON + temps'],
        ['Export', 'Selon permissions'],
      ],
      st: [
        ['Ouvrir la liste', 'Depuis le menu Formations ou la fiche parcours, accédez aux apprenants assignés.'],
        ['Filtrer', 'Utilisez la recherche par e-mail ou nom pour les gros effectifs.'],
        ['Analyser une ligne', 'Cliquez pour voir le détail : blocs visités, scores, certificats éventuels.'],
      ],
      se: [{ hd: 'RGPD', p: ['Les données sont personnelles : limitez l’export aux personnes habilitées et documentez la finalité.'] }],
      t: ['Pour les sessions présentielles, croisez la liste Lyrra avec votre feuille d’émargence officielle.'],
      r: ['app-progression', 'analytics-indicateurs'],
    },
  ),

  'app-groupes': d(
    'Les groupes (learning groups) servent à regrouper des apprenants pour des campagnes, du reporting ou des partages de contenu au niveau institution.',
    {
      h: [
        ['Création', 'Admin / RH'],
        ['Membres', 'Ajout manuel ou import'],
        ['Usage', 'Ciblage campagnes'],
      ],
      st: [
        ['Constituer un groupe', 'Créez le groupe dans l’espace institution puis ajoutez les membres ou synchronisez si un connecteur existe.'],
        ['Associer à une action', 'Sélectionnez le groupe lors du déploiement d’un parcours ou d’une communication.'],
        ['Maintenir', 'Mettez à jour les membres en fin de contrat ou de promotion pour des indicateurs justes.'],
      ],
      se: [{ hd: 'Données', p: ['Les effectifs de groupe peuvent apparaître dans les tableaux de bord RH — vérifiez les règles de confidentialité internes.'] }],
      t: ['Nommez les groupes de façon stable (« Promo 2025 A ») plutôt que des noms de personnes seules.'],
      r: ['inst-structure', 'app-inscriptions'],
    },
  ),

  'app-progression': d(
    'La progression stocke pour chaque inscription les blocs visités ou complétés, le temps passé, les scores et les dates de début / fin.',
    {
      h: [
        ['Complétion', 'Booléen + pourcentage estimé'],
        ['Blocs', 'Sessions par bloc'],
        ['Temps', 'Agrégé côté analytiques'],
      ],
      st: [
        ['Lire le statut', '« En cours », « Terminé », « Non commencé » dépendent des événements enregistrés par le player.'],
        ['Interpréter le temps', 'Le temps par bloc peut exclure les pauses lecteur fermé — utilisez-le comme ordre de grandeur.'],
        ['Agir sur les abandons', 'Identifiez les apprenants sans activité depuis X jours pour relances pédagogiques.'],
      ],
      se: [{ hd: 'Limites', p: ['Une progression à 100 % sans évaluation ne garantit pas la maîtrise — croisez avec quiz ou contrôle continu.'] }],
      t: ['Les correctifs de bugs player peuvent recalculer certaines métriques après déploiement.'],
      r: ['analytics-vue', 'eval-quiz'],
    },
  ),

  'app-certificats': d(
    'Les certificats attestent la réussite d’un parcours lorsque les conditions (blocs obligatoires, scores, Fin atteinte) sont remplies.',
    {
      h: [
        ['Génération', 'Automatique ou manuelle selon config'],
        ['Preuve', 'PDF / téléchargement'],
        ['Traçabilité', 'Associée à l’inscription'],
      ],
      st: [
        ['Vérifier les critères', 'Dans le designer, assurez-vous que la Fin est joignable et que les évaluations ont des seuils clairs.'],
        ['Contrôler les délivrances', 'L’interface liste les certificats émis ; en cas de litige, conservez l’historique.'],
        ['Communiquer la valeur', 'Précisez auprès des apprenants si le certificat vaut au titre d’une certification professionnelle ou d’une attestation interne.'],
      ],
      se: [{ hd: 'Qualiopi', p: ['Conservez la preuve d’évaluation et d’assiduité en complément du certificat pour le dossier qualité.'] }],
      t: ['En cas de changement de nom de formation, les certificats déjà émis conservent souvent l’ancien intitulé — anticipez avant campagne.'],
      r: ['qualite-tracabilite', 'pub-statuts'],
    },
  ),

  'medias-bibliotheque': d(
    'La bibliothèque média centralise fichiers audio, vidéo, images et documents pour les réutiliser dans les blocs EduFlow sans re-upload systématique.',
    {
      h: [
        ['Import', 'Glisser-déposer ou bouton'],
        ['Formats', 'MP4, MP3, PNG, PDF…'],
        ['Réutilisation', 'Référence dans plusieurs blocs'],
      ],
      st: [
        ['Importer', 'Choisissez un fichier respectant la taille max configurée sur l’instance ; renommez avant import pour faciliter la recherche.'],
        ['Organiser', 'Utilisez des préfixes ou dossiers logiques si l’interface le permet ; sinon convention de nommage d’équipe.'],
        ['Insérer', 'Dans un bloc, sélectionnez « depuis la bibliothèque » plutôt qu’un lien externe fragile.'],
      ],
      se: [{ hd: 'Stockage', p: ['Les médias lourds impactent la sauvegarde et le player : compressez vidéo/audio quand c’est possible.'] }],
      t: ['Vérifiez les droits d’utilisation (musique, images stock) avant publication publique.'],
      r: ['medias-video', 'eduflow-blocs'],
    },
  ),

  'medias-studio-audio': d(
    'Le studio audio permet d’enregistrer, d’éditer légèrement ou de générer de la voix (TTS) pour des contenus pédagogiques.',
    {
      h: [
        ['Pistes', 'Multi-pistes selon UI'],
        ['TTS', 'Voix et paramètres'],
        ['Export', 'Vers bibliothèque ou bloc'],
      ],
      st: [
        ['Préparer le script', 'Écrire un texte ponctué pour la synthèse vocale ; évitez les abréviations ambiguës.'],
        ['Enregistrer', 'Utilisez un micro correct et un environnement calme ; niveau son homogène entre les leçons.'],
        ['Contrôler l’écoute', 'Réécoutez intégralement avant publication — le TTS peut mal prononcer noms propres.'],
      ],
      se: [{ hd: 'Accessibilité', p: ['Proposez une version texte équivalente pour les contenus uniquement audio.'] }],
      t: ['Normalisez le volume entre les chapitres pour éviter les surprises enchaînées dans le player.'],
      r: ['medias-bibliotheque', 'marque-accessibilite'],
    },
  ),

  'medias-video': d(
    'Les vidéos intégrées dans Lyrra passent par upload ou URL selon le bloc ; le player HTML5 gère lecture, sous-titres si fournis.',
    {
      h: [
        ['Codec', 'H.264 + AAC fréquent'],
        ['Sous-titres', 'Fichier VTT recommandé'],
        ['Poids', 'Streaming & mobile'],
      ],
      st: [
        ['Encoder', 'Export 720p ou 1080p raisonnable ; bitrate modéré pour les réseaux mobiles des apprenants.'],
        ['Ajouter sous-titres', 'Indispensable pour l’accessibilité et les environnements sonores bruyants.'],
        ['Tester', 'Lecture sur Chrome / Safari mobile avant lancement de cohorte.'],
      ],
      se: [{ hd: 'Hébergement externe', p: ['Si vous pointez vers YouTube/Vimeo, respectez la politique de confidentialité (cookies, tracking) de votre organisme.'] }],
      t: ['Une vidéo de plus de 15 minutes sans découpage peut faire chuter l’engagement — découpez en blocs.'],
      r: ['medias-bibliotheque', 'eduflow-blocs'],
    },
  ),

  'medias-documents': d(
    'Les PDF et documents téléchargeables complètent le parcours : supports de cours, fiches mémo, modèles à remplir.',
    {
      h: [
        ['PDF', 'Visionneuse intégrée ou download'],
        ['Accessibilité', 'PDF balisé de préférence'],
        ['Version', 'Numéro dans le nom de fichier'],
      ],
      st: [
        ['Préparer le fichier', 'Réduisez la taille, vérifiez l’orientation et la lisibilité mobile.'],
        ['Joindre au bloc', 'Utilisez le champ document du bloc concerné ; testez le téléchargement en navigation privée.'],
        ['Mettre à jour', 'Lors d’un changement de version, remplacez le fichier et informez les apprenants en cours si nécessaire.'],
      ],
      se: [{ hd: 'Propriété intellectuelle', p: ['Ne diffusez que des documents pour lesquels vous avez les droits de reproduction.'] }],
      t: ['Nommez « v2_2025_03_module3.pdf » pour éviter confusion avec d’anciennes versions.'],
      r: ['marque-accessibilite', 'eduflow-blocs'],
    },
  ),

  'podcasts-creation': d(
    'Le module podcast permet de structurer des épisodes long format, métadonnées et visuels de couverture, distincts du découpage bloc par bloc du EduFlow.',
    {
      h: [
        ['Épisodes', 'Titre, durée, ordre'],
        ['Audio', 'Fichier source haute qualité'],
        ['Image', 'Cover carré recommandé'],
      ],
      st: [
        ['Créer l’émission', 'Définissez titre, description et langue ; cohérent avec votre charte éditoriale.'],
        ['Ajouter épisodes', 'Uploadez l’audio final ; vérifiez loudness -16 LUFS approximatif pour confort d’écoute.'],
        ['Publier', 'Passez le statut adéquat pour rendre disponible aux abonnés ou groupe cible.'],
      ],
      se: [{ hd: 'Différence avec EduFlow', p: ['Un podcast sert à du linéaire audio ; un EduFlow sert à un parcours interactif avec évaluations.'] }],
      t: ['Ajoutez des chapitres ou titres d’épisode explicites pour la recherche dans les apps de podcast.'],
      r: ['podcasts-diffusion', 'medias-studio-audio'],
    },
  ),

  'podcasts-diffusion': d(
    'La diffusion contrôle qui peut écouter (institution, public, lien) et alimente les statistiques d’écoute lorsqu’elles sont exposées.',
    {
      h: [
        ['Cible', 'Interne / externe'],
        ['Partage', 'Lien ou intégration'],
        ['Mesure', 'Écoutes agrégées'],
      ],
      st: [
        ['Choisir le périmètre', 'Restreignez aux membres de l’établissement pour du contenu confidentiel.'],
        ['Communiquer', 'Envoyez le lien officiel ; évitez les copies de fichiers MP3 non contrôlées pour du contenu payant.'],
        ['Analyser', 'Utilisez les stats pour décider des sujets de saisons suivantes.'],
      ],
      se: [{ hd: 'Archivage', p: ['Les podcasts supprimés peuvent passer par la corbeille institution — vérifiez la procédure de restauration.'] }],
      t: ['Pour un podcast RH interne, synchronisez la sortie avec la communication managers.'],
      r: ['inst-trash', 'analytics-indicateurs'],
    },
  ),

  'eval-quiz': d(
    'Les quiz à choix multiples permettent feedback immédiat, barème, nombre de tentatives et parfois branchement selon score.',
    {
      h: [
        ['Questions', 'Énoncé + distracteurs'],
        ['Barème', 'Points par question'],
        ['Feedback', 'Juste / faux pédagogique'],
      ],
      st: [
        ['Rédiger', 'Une seule bonne réponse claire ; distracteurs plausibles mais non ambigus.'],
        ['Configurer la notation', 'Renseignez le scoring dans le bloc pour lever l’avertissement de validation.'],
        ['Tester', 'Passez le quiz en mode apprenant avec les trois profils : bon, moyen, mauvais.'],
      ],
      se: [{ hd: 'Anti-triche', p: ['Limitez les tentatives et randomisez l’ordre des questions si le player l’offre.'] }],
      t: ['Évitez le double négatif dans les énoncés — source fréquente de contestation.'],
      r: ['eval-ouverte', 'eduflow-validation'],
    },
  ),

  'eval-ouverte': d(
    'Les questions ouvertes collectent des réponses textuelles ; la correction est souvent manuelle ou semi-assistée.',
    {
      h: [
        ['Consigne', 'Critères d’évaluation explicites'],
        ['Correction', 'Charge formateur'],
        ['Traçabilité', 'Historique des réponses'],
      ],
      st: [
        ['Rédiger la question', 'Indiquez longueur attendue, exemple de réponse complète et grille de notation interne.'],
        ['Prévoir la charge', 'Pour 200 apprenants, prévoyez un outillage ou une équipe de correcteurs.'],
        ['Rendre les copies', 'Utilisez les commentaires dans l’outil ou exportez pour correction externe selon process.'],
      ],
      se: [{ hd: 'RGPD', p: ['Les réponses libres peuvent contenir des données sensibles — limitez la visibilité.'] }],
      t: ['Pour l’oral simulé, préférez un enregistrement audio dédié avec consentement explicite.'],
      r: ['eval-quiz', 'app-inscriptions'],
    },
  ),

  'eval-auto-eval': d(
    'L’auto-évaluation et la remédiation invitent l’apprenant à se positionner puis à consommer des ressources ciblées selon le résultat.',
    {
      h: [
        ['Positionnement', 'Grille ou échelle'],
        ['Ressources', 'Liens conditionnels'],
        ['Pédagogie', 'Métacognition'],
      ],
      st: [
        ['Concevoir la grille', 'Alignez les critères sur les objectifs du module.'],
        ['Brancher le parcours', 'Utilisez les sorties multiples pour diriger vers remédiation ou approfondissement.'],
        ['Mesurer l’effet', 'Comparez scores avant/après sur un quiz sommatif.'],
      ],
      se: [{ hd: 'Sincérité', p: ['L’auto-évaluation seule ne suffit pas à certifier — combinez avec une évaluation externe pour les diplômes.'] }],
      t: ['Affichez un message rassurant : l’auto-évaluation est un outil de progrès, pas un piège.'],
      r: ['eduflow-branches', 'eduflow-objectifs'],
    },
  ),

  'analytics-vue': d(
    'La page Analytiques offre des graphiques personnalisables (ligne, barres, camembert, aires), filtres par période et par formation, disposition sauvegardée localement.',
    {
      h: [
        ['Filtres', '7j à 12 mois, parcours'],
        ['Données', 'Issues de la base (inscriptions, sessions)'],
        ['Layout', 'localStorage navigateur'],
      ],
      st: [
        ['Choisir la période', 'Les courtes périodes pour le pilotage tactique ; 90 jours ou 12 mois pour les tendances.'],
        ['Filtrer une formation', '« Toutes les formations » agrège vos parcours accessibles ; une formation isolée affine le diagnostic.'],
        ['Réorganiser', 'Glissez les cartes, changez la taille S/M/L ; ajoutez ou retirez des graphiques selon votre besoin de reporting.'],
      ],
      se: [{ hd: 'Interprétation', p: ['Un pic de complétions peut correspondre à une relance RH — croisez toujours avec le contexte métier.'] }],
      t: ['Réinitialisez la disposition si après une mise à jour l’affichage semble incohérent.'],
      r: ['analytics-indicateurs', 'app-progression'],
    },
  ),

  'analytics-indicateurs': d(
    'Indicateurs typiques : taux de complétion moyen par fenêtre temporelle, répartition statuts (terminé / en cours / non commencé), temps d’engagement sur blocs, apprenants distincts par bloc.',
    {
      h: [
        ['Complétion', 'Basée sur événements player'],
        ['Statuts', '3 états principaux'],
        ['Blocs', 'Sessions dans la période'],
      ],
      st: [
        ['Lire le camembert statuts', 'Un gros « non commencé » après invitation = problème de communication ou de liens.'],
        ['Analyser la ligne complétion', 'Moyenne par créneau temporel parmi les actifs — zéro peut signifier peu d’activité sur la période.'],
        ['Prioriser les blocs', 'Les blocs avec peu d’apprenants distincts peuvent être trop difficiles ou mal placés dans le parcours.'],
      ],
      se: [{ hd: 'Limites', p: ['Sans session complétée, le temps d’engagement sous-estime l’effort — interprétez avec prudence.'] }],
      t: ['Exportez périodiquement vers Excel pour archives si votre instance le permet.'],
      r: ['analytics-exports', 'demarrage-tableau-bord'],
    },
  ),

  'analytics-exports': d(
    'Les exports servent au reporting RH, financier ou qualité : tableaux de progression, listes d’inscrits, preuves de suivi.',
    {
      h: [
        ['Formats', 'CSV / XLS selon modules'],
        ['Périmètre', 'Filtres appliqués'],
        ['Fréquence', 'Mensuelle recommandée'],
      ],
      st: [
        ['Préparer l’export', 'Appliquez les filtres (dates, formation) avant export pour un fichier exploitable.'],
        ['Pseudonymiser', 'Si vous partagez à un tiers, retirez les colonnes directement identifiantes quand possible.'],
        ['Archiver', 'Stockez sur un référentiel sécurisé avec politique de rétention alignée RGPD.'],
      ],
      se: [{ hd: 'Qualiopi', p: ['Conservez les exports signés ou horodatés comme preuve d’activité pour les audits.'] }],
      t: ['Automatisez les exports récurrents si une API ou un connecteur est disponible sur votre offre.'],
      r: ['donnees-conservation', 'qualite-tracabilite'],
    },
  ),

  'inst-collaborateurs': d(
    'L’administrateur d’établissement crée ou invite des utilisateurs, assigne les rôles (formateur, RH, etc.) et gère les accès aux modules sensibles.',
    {
      h: [
        ['Invitations', 'E-mail + lien'],
        ['Rôles', 'Principe du moindre privilège'],
        ['Audit', 'Qui a publié quoi'],
      ],
      st: [
        ['Inviter', 'Depuis l’espace institution, ajoutez l’e-mail et le rôle ; l’utilisateur complète son profil à la première connexion.'],
        ['Révoquer', 'Désactivez les comptes partants (fin de contrat) pour sécurité et conformité.'],
        ['Former', 'Un court guide interne sur les rôles évite les erreurs de publication.'],
      ],
      se: [{ hd: 'SSO', p: ['Si votre instance utilise SSO entreprise, la création de compte peut être automatique — alignez-vous avec l’IT.'] }],
      t: ['Évitez les comptes partagés « formation@… » : inauditable en cas d’incident.'],
      r: ['demarrage-roles', 'donnees-mots-de-passe'],
    },
  ),

  'inst-structure': d(
    'La structure définit ce qui appartient à l’établissement (tous les parcours des formateurs internes, corbeille commune, charte) versus espaces personnels.',
    {
      h: [
        ['Périmètre', 'Institution vs perso'],
        ['Visibilité', 'Partage inter-formateurs'],
        ['Catalogue', 'Offre globale'],
      ],
      st: [
        ['Cartographier', 'Listez qui produit du contenu et sous quel compte pour clarifier la propriété intellectuelle.'],
        ['Standardiser', 'Modèles de parcours « maison » dupliqués par les formateurs accélèrent le déploiement.'],
        ['Superviser', 'Les admins voient souvent une vue agrégée — utilisez-la pour équilibrer la charge.'],
      ],
      se: [{ hd: 'Multi-sites', p: ['Les structures multi-établissements peuvent avoir des cloisonnements — vérifiez votre contrat.'] }],
      t: ['Documentez la procédure quand un formateur quitte l’organisation (transfert de propriété des parcours).'],
      r: ['inst-trash', 'marque-charte'],
    },
  ),

  'inst-trash': d(
    'La corbeille institutionnelle conserve formations et podcasts supprimés (soft delete) pour restauration ou purge contrôlée.',
    {
      h: [
        ['Restauration', 'Retour en ligne'],
        ['Filtres', 'Par type / date'],
        ['Droits', 'Admin institution'],
      ],
      st: [
        ['Accéder', 'Espace établissement, section corbeille ou équivalent selon votre build.'],
        ['Restaurer', 'Sélectionnez l’élément puis restaurez — vérifiez les dépendances (médias liés).'],
        ['Purger', 'Ne purgez définitivement qu’après validation juridique / archivage externe.'],
      ],
      se: [{ hd: 'RGPD', p: ['La suppression ne supprime pas toujours immédiatement les preuves légales — respectez la durée légale de conservation.'] }],
      t: ['Formez les formateurs : suppression ≠ disparition instantanée ; évitez les fuites de stress.'],
      r: ['donnees-conservation', 'pub-statuts'],
    },
  ),

  'marque-charte': d(
    'La charte institutionnelle (couleurs primaires, logo) se propage souvent au player et aux espaces apprenants pour une expérience cohérente.',
    {
      h: [
        ['Couleurs', 'Variables CSS institution'],
        ['Logo', 'Fichier haute définition'],
        ['Player', 'Rendu temps réel'],
      ],
      st: [
        ['Configurer', 'Dans les paramètres d’établissement / branding, uploadez logo et choisissez la teinte dominante.'],
        ['Tester', 'Ouvrez un parcours en prévisualisation apprenant pour valider contraste et lisibilité.'],
        ['Valider', 'Faites relire par communication interne avant déploiement large.'],
      ],
      se: [{ hd: 'Accessibilité', p: ['Un primaire trop clair sur blanc échoue au WCAG — utilisez les outils de contraste.'] }],
      t: ['Gardez une version « sombre » cohérente si vos apprenants utilisent le mode nuit.'],
      r: ['marque-accessibilite', 'marque-landing'],
    },
  ),

  'marque-accessibilite': d(
    'L’accessibilité couvre contrastes, textes alternatifs pour images, structure des titres, lecteurs d’écran et clavier dans le player.',
    {
      h: [
        ['WCAG', 'Cible AA recommandée'],
        ['Médias', 'Sous-titres, transcriptions'],
        ['Contenu', 'Titres de blocs explicites'],
      ],
      st: [
        ['Images', 'Renseignez texte alternatif décrivant l’information portée, pas « image1 ».'],
        ['Vidéo', 'Fournissez sous-titres synchronisés ; transcriptions pour contenus longs.'],
        ['Quiz', 'Formulations claires ; boutons et zones cliquables suffisamment grands sur mobile.'],
      ],
      se: [{ hd: 'Audit', p: ['Réalisez un audit annuel ou à chaque refonte majeure du player.'] }],
      t: ['Testez au clavier uniquement : tabulation à travers le parcours complet.'],
      r: ['medias-video', 'eduflow-blocs'],
    },
  ),

  'marque-landing': d(
    'Les pages publiques ou vitrine présentent votre catalogue et votre valeur aux visiteurs externes lorsque le module est activé.',
    {
      h: [
        ['SEO', 'Titres & meta'],
        ['Contenu', 'Offre claire'],
        ['Conversion', 'Appel à l’action inscription'],
      ],
      st: [
        ['Rédiger', 'Mettez en avant différenciateurs, publics cibles et preuves sociales autorisées.'],
        ['Relier au catalogue', 'Les fiches formation doivent être à jour pour éviter les promesses fausses.'],
        ['Mesurer', 'Suivez trafic et taux de conversion vers inscription ou contact.'],
      ],
      se: [{ hd: 'Mentions légales', p: ['Éditeur, hébergeur, politique de confidentialité doivent être accessibles depuis le footer.'] }],
      t: ['Alignez les visuels avec la même charte que le player pour une reconnaissance de marque.'],
      r: ['pub-catalogue', 'marque-charte'],
    },
  ),

  'qualite-qualiopi': d(
    'Les espaces Qualiopi aident à préparer indicateurs, preuves et auto-positionnement par critère ; le détail dépend du paramétrage de votre instance.',
    {
      h: [
        ['Critères', '7 processus habituels'],
        ['Preuves', 'Pièces jointes / liens'],
        ['Veille', 'Mise à jour annuelle'],
      ],
      st: [
        ['Renseigner', 'Pour chaque critère, indiquez statut, actions et échéances ; joignez exports Lyrra pertinents.'],
        [
          'Croiser',
          'Utilisez objectifs pédagogiques, évaluations et analytics comme preuves d’efficacité.',
        ],
        [
          'Auditer',
          'Simulez une visite certificateur : un observateur externe doit retrouver chaque preuve en moins de 10 minutes.',
        ],
      ],
      se: [{ hd: 'Responsabilité', p: ['Lyrra facilite la collecte ; la conformité Qualiopi reste sous la responsabilité de l’organisme de formation.'] }],
      t: ['Exportez un snapshot PDF des tableaux de bord avant chaque audit pour figer les chiffres.'],
      r: ['qualite-tracabilite', 'eduflow-objectifs'],
    },
  ),

  'qualite-tracabilite': d(
    'La traçabilité relie objectifs, contenus vus, temps passé, évaluations et décisions (certification, exclusion) pour reconstituer le parcours d’un apprenant.',
    {
      h: [
        ['Journal', 'Événements horodatés'],
        ['Identité', 'Lien compte / participant'],
        ['Conservation', 'Durée légale'],
      ],
      st: [
        [
          'Configurer',
          'Assurez-vous que chaque bloc critique (évaluation, attestation) logue une trace.',
        ],
        [
          'Contrôler',
          'En cas de litige, extrayez la chronologie complète pour le juridique.',
        ],
        [
          'Archiver',
          'Ne détruisez pas avant la fin des délais prescription ou obligations Qualiopi.',
        ],
      ],
      se: [{ hd: 'Anonymisation', p: ['Après délai, l’anonymisation peut être possible pour les données non nécessaires.'] }],
      t: ['Documentez la correspondance entre identifiant Lyrra et dossier papier si coexistence.'],
      r: ['donnees-conservation', 'app-certificats'],
    },
  ),

  'donnees-rgpd': d(
    'Lyrra traite des données personnelles (identité, progression, parfois biométrie ou e-mails métier) en tant qu’outil ; vous êtes responsable du traitement pour votre organisme.',
    {
      h: [
        ['Rôles', 'Responsable vs sous-traitant'],
        ['Finalités', 'Formation, certification, RH'],
        ['Base légale', 'Contrat, obligation légale, consentement'],
      ],
      st: [
        [
          'Tenir le registre',
          'Listez traitements Lyrra : quelles données, qui accède, durée, sécurité.',
        ],
        [
          'Informer',
          'Les apprenants doivent avoir une politique de confidentialité accessible avant collecte.',
        ],
        [
          'Répondre aux droits',
          'Accès, rectification, effacement, limitation — délais réglementaires.',
        ],
      ],
      se: [{ hd: 'Transferts hors UE', p: ['Si des sous-traitants sont US-based, vérifiez clauses types ou décision d’adéquation.'] }],
      t: ['Ne mélangez pas données pros et persos dans un même export sans base légale claire.'],
      r: ['donnees-conservation', 'inst-collaborateurs'],
    },
  ),

  'donnees-conservation': d(
    'Les durées de conservation varient : preuves de formation souvent plusieurs années après fin de session ; données de prospection plus courtes.',
    {
      h: [
        ['Actif', 'Pendant la relation de formation'],
        ['Archivage', 'Accès restreint'],
        ['Destruction', 'Procédure sécurisée'],
      ],
      st: [
        [
          'Définir la politique',
          'Validez avec votre DPO / juridique des durées par type de donnée.',
        ],
        [
          'Configurer',
          'Adaptez workflows internes (exports annuels puis purge des brouillons).',
        ],
        ['Prouver', 'En audit, montrez la procédure même si la technique est manuelle.'],
      ],
      se: [{ hd: 'Preuves', p: ['Certaines données doivent être conservées même si l’apprenant demande effacement — informez-le.'] }],
      t: ['Ne conservez pas indéfiniment les comptes inactifs sans relance — principe de minimisation.'],
      r: ['inst-trash', 'donnees-rgpd'],
    },
  ),

  'donnees-mots-de-passe': d(
    'Les mots de passe doivent être robustes ; le SSO entreprise délègue l’authentification à votre IdP.',
    {
      h: [
        ['Politique', 'Longueur, complexité'],
        ['MFA', 'Si activé par l’IT'],
        ['Sessions', 'Expiration auto'],
      ],
      st: [
        ['Choisir', 'Phrase de passe longue > mot court complexe oublié.'],
        ['Ne pas réutiliser', 'Un leak sur un autre site ne doit pas compromettre Lyrra.'],
        ['Signaler', 'Toute session suspecte à l’administrateur.'],
      ],
      se: [{ hd: 'Comptes service', p: ['Les comptes techniques doivent avoir rotation de secrets documentée.'] }],
      t: ['Activez la déconnexion sur les postes partagés après chaque session.'],
      r: ['support-faq', 'demarrage-profil'],
    },
  ),

  'support-faq': d(
    'Problèmes fréquents : média qui ne charge pas (format, CORS, URL expirée), parcours qui ne se publie pas (erreurs validation), apprenant bloqué (cache navigateur).',
    {
      h: [
        ['Étape 1', 'Reproduire en navigation privée'],
        ['Étape 2', 'Autre navigateur'],
        ['Étape 3', 'Capturer erreur console si possible'],
      ],
      st: [
        [
          'Média',
          'Vérifiez que le fichier existe encore et que l’URL signée n’a pas expiré.',
        ],
        ['Publication', 'Ouvrez Validation et corrigez les erreurs rouges.'],
        ['Player blanc', 'Désactivez extensions adblock sur le domaine Lyrra.'],
      ],
      se: [{ hd: 'Escale', p: ['Préparez ID parcours, ID apprenant (hash), heure UTC, capture d’écran.'] }],
      t: ['Le rafraîchissement forcé (Ctrl+F5) résout souvent les assets JS en cache après déploiement.'],
      r: ['support-navigateur', 'support-contact'],
    },
  ),

  'support-navigateur': d(
    'Lyrra cible les navigateurs modernes (Chrome, Firefox, Safari, Edge récents). Internet Explorer n’est pas supporté.',
    {
      h: [
        ['Version', 'Dernière maj stable'],
        ['Cookies', 'Tiers si SSO'],
        ['WebGL', 'Rare pour canvas designer'],
      ],
      st: [
        ['Mettre à jour', 'Appliquez les correctifs sécurité du navigateur.'],
        ['Autoriser', 'Cookies et stockage local pour rester connecté.'],
        [
          'Tester mobile',
          'Safari iOS peut avoir des limites lecture média — testez sur device réel.',
        ],
      ],
      se: [{ hd: 'Réseau', p: ['Proxys d’entreprise peuvent bloquer WebSocket ou uploads — contacter l’IT.'] }],
      t: ['Désactivez les VPN expirimentaux si uploads échouent systématiquement.'],
      r: ['support-faq', 'medias-video'],
    },
  ),

  'support-contact': d(
    'Le support Lyrra (ou votre intégrateur) traite les bugs et questions contractuelles ; votre admin interne traite les comptes et rôles.',
    {
      h: [
        ['Niveau 1', 'Admin établissement'],
        ['Niveau 2', 'Éditeur Lyrra'],
        ['SLA', 'Selon contrat'],
      ],
      st: [
        ['Qualifier', 'Fonctionnel vs technique vs demande d’évolution.'],
        [
          'Documenter',
          'Pas à pas, captures, URL, heure, utilisateur impacté (sans données sensibles en clair dans le titre).',
        ],
        ['Suivre', 'Numéro de ticket ; relancez avec éléments nouveaux seulement.'],
      ],
      se: [{ hd: 'Urgences', p: ['Perte de données ou sécurité : canal prioritaire défini au contrat.'] }],
      t: ['Un ticket « ça marche pas » sans contexte est relégué en fin de file — soyez précis.'],
      r: ['support-faq', 'donnees-rgpd'],
    },
  ),

  'api-rest-openapi': d(
    'Chaque instance Lyrra expose une documentation HTTP interactive (Swagger UI) et un fichier OpenAPI JSON, générés à partir du code backend.',
    {
      h: [
        ['Swagger UI', API_DOC_PATHS.swagger],
        ['OpenAPI JSON', API_DOC_PATHS.openapiJson],
        ['Santé', API_DOC_PATHS.health],
      ],
      st: [
        [
          'Ouvrir Swagger',
          `Dans le navigateur : même origine que l’application + ${API_DOC_PATHS.swagger}. Vous y voyez chaque méthode (GET, POST, …) et chemin exact.`,
        ],
        [
          'Authentification',
          'La plupart des routes exigent un cookie de session ou un en-tête Bearer. Swagger peut envoyer des requêtes si vous configurez les en-têtes ou les cookies dans le navigateur.',
        ],
        [
          'Préfixes principaux',
          'Les modules REST sont regroupés par préfixe (auth, eduflows, institution, v2, …). La liste à jour est dans l’onglet Documentation du panneau MCP institution.',
        ],
      ],
      se: [
        {
          hd: 'Préfixes V1 (extraits)',
          p: [
            `La plateforme compte ${API_V1_ROUTE_MODULES.length} préfixes V1 documentés dans l’app (ex. /api/eduflows pour les parcours, /api/auth pour la connexion).`,
          ],
          b: API_V1_ROUTE_MODULES.slice(0, 12).map((m) => `${m.prefix} — ${m.description}`),
        },
        {
          hd: 'API V2',
          p: [
            `Toutes les routes métier V2 sont sous /api/v2 (actuellement ${API_V2_ROUTE_MODULES.length} sous-modules : campagnes, catalogue, intégrations, etc.).`,
          ],
          b: API_V2_ROUTE_MODULES.map((m) => `${m.prefix} — ${m.description}`),
        },
      ],
      t: [
        'En cas de doute sur un chemin, Swagger fait foi : il est régénéré depuis les routeurs Express.',
      ],
      r: ['mcp-lyrra', 'donnees-rgpd'],
    },
  ),

  'mcp-lyrra': d(
    'Le Model Context Protocol permet à un client IA (Claude Desktop, Cursor, etc.) d’appeler des outils qui dialoguent avec Lyrra via l’API HTTP, en utilisant des clés API.',
    {
      h: [
        ['Serveur MCP', 'Dépôt : lyrra-studio-app/apps/mcp-server (stdio, Node 20+)'],
        ['Auth clé API', 'POST /api/auth/api-key/token → JWT pour les appels'],
        ['Base API', '/api'],
      ],
      st: [
        [
          'Clés institution',
          'Dans Établissement → MCP → Clés API : création de paires pour vos concepteurs. Endpoints REST : lister les routes ci-dessous.',
        ],
        [
          'Clés personnelles',
          'Dans Paramètres → Clés API (MCP & API) : clés rattachées à votre compte utilisateur.',
        ],
        [
          'Variables d’environnement',
          'LYRRA_API_URL (…/api), LYRRA_CLIENT_ID + LYRRA_CLIENT_SECRET ou LYRRA_ACCESS_TOKEN (JWT). Optionnel : LYRRA_OPENAPI_URL, LYRRA_MCP_MAX_TOOLS.',
        ],
      ],
      se: [
        {
          hd: 'Catalogue in-app',
          p: [
            'Établissement → MCP → Documentation : la liste exhaustive correspond à chaque opération HTTP du backend (OpenAPI). Une ligne = une méthode + un chemin ; le nom d’outil MCP recommandé est l’`operationId` ; la description longue inclut le fichier routeur et des indications d’authentification.',
          ],
        },
        {
          hd: 'Endpoints REST — clés institution',
          p: ['Utilisés par le tableau de bord institution pour gérer les identifiants MCP.'],
          b: [
            MCP_HTTP_ENDPOINTS.institutionKeys.list,
            MCP_HTTP_ENDPOINTS.institutionKeys.create,
            MCP_HTTP_ENDPOINTS.institutionKeys.updateName,
            MCP_HTTP_ENDPOINTS.institutionKeys.toggle,
            MCP_HTTP_ENDPOINTS.institutionKeys.remove,
          ],
        },
        {
          hd: 'Endpoints REST — clés personnelles',
          p: ['Compte concepteur (paramètres).'],
          b: [
            MCP_HTTP_ENDPOINTS.userKeys.list,
            MCP_HTTP_ENDPOINTS.userKeys.create,
            MCP_HTTP_ENDPOINTS.userKeys.toggle,
            MCP_HTTP_ENDPOINTS.userKeys.remove,
          ],
        },
      ],
      t: [
        'Claude Desktop : utilisez `command: npx` et `args: ["-y", "@lyrra/mcp-server"]` avec les variables d’environnement (voir Établissement → MCP). Développeurs du monorepo : alternative locale avec `node` et le chemin absolu vers `apps/mcp-server/dist/index.js` après `npm run build`.',
      ],
      r: ['api-rest-openapi', 'inst-collaborateurs'],
    },
  ),
};
